import type { StreamParser } from '@codemirror/language';
import { StreamLanguage } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

interface LexQueryState {
  inBlockComment: boolean;
  inMetadata: boolean;
}

// A comprehensive CM6 stream parser for Lexalytics' LexQuery syntax.
// It covers strings, keywords, operators, comments, entity markers, wildcards, and special syntax.
const lexQueryParser: StreamParser<LexQueryState> = {
  // Map legacy stream token names to Lezer tags so HighlightStyle works
  tokenTable: {
    keyword: t.keyword,
    string: t.string,
    number: t.number,
    atom: t.atom,
    operator: t.operator,
    comment: t.comment,
    bracket: t.bracket,
    type: t.typeName,
    term: t.variableName,
    wildcard: t.regexp,
    meta: t.meta,
    modifier: t.modifier,
    error: t.invalid,
    tag: t.keyword,
    // Custom tokens used by our grammar
    boolean: t.definitionKeyword, // AND, OR
    proximity: t.moduleKeyword, // NEAR, ONEAR, NOTNEAR
    sentence: t.controlKeyword, // WITH, NOTWITH, NEARWITH
    exclusion: t.operatorKeyword, // NOT, EXCLUDE
    order: t.keyword, // FOLLOW, FOLLOWWITH
  },
  startState() {
    return { inBlockComment: false, inMetadata: false };
  },
  token(stream, state) {
    // Handle block comment continuation
    if (state.inBlockComment) {
      if (stream.skipTo('*/')) {
        stream.match('*/');
        state.inBlockComment = false;
      } else {
        stream.skipToEnd();
      }
      return 'comment';
    }

    // Skip spaces
    if (stream.eatSpace()) {
      return null;
    }

    // Line comment
    if (stream.match('//')) {
      stream.skipToEnd();
      return 'comment';
    }

    // Block comment start
    if (stream.match('/*')) {
      state.inBlockComment = true;
      if (stream.skipTo('*/')) {
        stream.match('*/');
        state.inBlockComment = false;
      } else {
        stream.skipToEnd();
      }
      return 'comment';
    }

    // Strings: double quoted with escapes
    if (stream.peek() === '"') {
      stream.next(); // consume opening quote
      let escaped = false;
      let ch;
      while ((ch = stream.next()) != null) {
        if (ch === '"' && !escaped) {
          break;
        }
        escaped = !escaped && ch === '\\';
      }
      return 'string';
    }

    // Negation and case sensitivity modifiers (+ and - prefixes, ~ for case sensitivity)
    if (stream.match(/^[+\-~][A-Za-z_][A-Za-z0-9_\-]*/)) {
      return 'modifier';
    }

    // ^atom markers like ^entity (nested query references)
    if (stream.match(/^\^[A-Za-z0-9_\-]+/)) {
      return 'atom';
    }

    // Wildcards: prefix or suffix
    if (
      stream.match(/^\*[A-Za-z][A-Za-z0-9_\-]{2,}/) ||
      stream.match(/^[A-Za-z][A-Za-z0-9_\-]{2,}\*/)
    ) {
      return 'wildcard';
    }

    // NLP features and metadata references with curly braces
    if (stream.match(/^\{/)) {
      // Start of metadata expression
      state.inMetadata = true;
      return 'meta';
    }

    if (state.inMetadata) {
      // Handle entity types within metadata expressions
      if (stream.match(/^entity\s+/i)) {
        return 'meta';
      }

      // Handle entity types specifically
      if (stream.match(/^(?:company|person|place|product|phone|email|url|money|percent|date)\b/i)) {
        return 'type';
      }

      // Handle numbers within metadata expressions
      if (stream.match(/^[+-]?(?:\d+\.\d+|\d+|\.\d+)(?:e[+-]?\d+)?/i)) {
        return 'number';
      }

      // Handle closing brace
      if (stream.match(/^\}/)) {
        state.inMetadata = false;
        return 'meta';
      }

      // Handle other content within metadata
      if (stream.match(/^[^}\d]+/)) {
        return 'meta';
      }

      // Fallback: consume one character
      stream.next();
      return 'meta';
    }

    // Numbers: hex or decimal with optional exponent
    if (stream.match(/^0x[0-9a-fA-F]+/)) {
      return 'number';
    }
    if (stream.match(/^[+-]?(?:\d+\.\d+|\d+|\.\d+)(?:e[+-]?\d+)?/i)) {
      return 'number';
    }

    // Logical boolean operators (symbolic) must be checked BEFORE generic operators
    if (stream.match(/^(?:&&|\|\|)/)) {
      return 'boolean';
    }

    // Operators
    if (stream.match(/^[-+*=<>!~&|]+/)) {
      return 'operator';
    }

    // Braces/parens
    if (stream.match(/^[{}()\[\]]/)) {
      return 'bracket';
    }

    // Boolean operators (AND, OR)
    if (stream.match(/^(?:AND|OR)\b/i)) {
      return 'boolean';
    }

    // Proximity operators (NEAR, ONEAR, etc.)
    if (stream.match(/^(?:O?NEAR(?:\/\d+)?|NOTNEAR(?:\/\d+)?)\b/i)) {
      return 'proximity';
    }

    // Sentence-level operators (WITH, NOTWITH, etc.)
    if (stream.match(/^(?:WITH|NOTWITH|NEARWITH(?:\/\d+)?|ONEARWITH(?:\/\d+)?)\b/i)) {
      return 'sentence';
    }

    // Exclusion operators (NOT, EXCLUDE)
    if (stream.match(/^(?:NOT|EXCLUDE)\b/i)) {
      return 'exclusion';
    }

    // Order operators (FOLLOW, FOLLOWWITH)
    if (stream.match(/^(?:FOLLOW(?:\/\d+)?|FOLLOWWITH(?:\/\d+)?)\b/i)) {
      return 'order';
    }

    // Entity type markers
    if (stream.match(/^(?:PERSON|PLACE|COMPANY|PRODUCT|PHONE|EMAIL|URL|MONEY|PERCENT|DATE)\b/i)) {
      return 'type'; // Using standard type token
    }

    // Consume a single word or character
    if (stream.match(/^[A-Za-z_][A-Za-z0-9_\-]*/)) {
      return 'term'; // plain identifier/text
    }

    // Fallback: advance one char
    stream.next();
    return null;
  },
};

export const lexQueryLanguage = StreamLanguage.define(lexQueryParser);
